import React, { useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Mail, Lock, User, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { cn } from '../../lib/utils';
interface AuthScreenProps {
  isOpen: boolean;
  onClose: () => void;
  onAuthSuccess: (user: {
    email: string;
    name: string;
  }) => void;
}
type AuthMode = 'signin' | 'signup' | 'reset' | 'verify';
export function AuthScreen({
  isOpen,
  onClose,
  onAuthSuccess
}: AuthScreenProps) {
  const [mode, setMode] = useState<AuthMode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [verificationSent, setVerificationSent] = useState(false);
  const handleEmailAuth = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    // Basic validation
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email');
      return;
    }
    if (mode === 'signup' && !name) {
      setError('Please enter your name');
      return;
    }
    if ((mode === 'signin' || mode === 'signup') && password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    // Mock authentication
    if (mode === 'reset') {
      setMode('verify');
      setVerificationSent(true);
    } else if (mode === 'signup') {
      setMode('verify');
      setVerificationSent(true);
    } else {
      onAuthSuccess({
        email,
        name: name || 'Player'
      });
    }
  };
  const handleSocialAuth = (provider: string) => {
    // Mock social auth
    onAuthSuccess({
      email: `user@${provider}.com`,
      name: 'Player'
    });
  };
  const handleResendVerification = () => {
    setVerificationSent(true);
    setTimeout(() => setVerificationSent(false), 3000);
  };
  return <Modal isOpen={isOpen} onClose={onClose} className="max-w-md" data-id="element-12">
      <div className="space-y-6" data-id="element-13">
        {/* Header */}
        <div className="text-center" data-id="element-14">
          {mode !== 'signin' && <button onClick={() => setMode('signin')} className="absolute left-6 top-6 p-2 text-slate-400 hover:bg-slate-100 rounded-full" data-id="element-15">
              <ArrowLeft size={20} data-id="element-16" />
            </button>}
          <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg" data-id="element-17">
            <img src="https://cdn.magicpatterns.com/uploads/cSWDoJ881q2Jhi42ScyEen/Connectiply.png" alt="Connectiply" className="w-12 h-12 object-contain" data-id="element-18" />
          </div>
          <h2 className="text-2xl font-black text-slate-900" data-id="element-19">
            {mode === 'signin' && 'Welcome Back'}
            {mode === 'signup' && 'Create Account'}
            {mode === 'reset' && 'Reset Password'}
            {mode === 'verify' && 'Check Your Email'}
          </h2>
          <p className="text-sm text-slate-500 mt-1" data-id="element-20">
            {mode === 'signin' && 'Sign in to continue playing'}
            {mode === 'signup' && 'Join Connectiply today'}
            {mode === 'reset' && "We'll send you a reset link"}
            {mode === 'verify' && 'Verification link sent'}
          </p>
        </div>

        {mode === 'verify' ?
      // Verification Screen
      <div className="text-center space-y-4" data-id="element-21">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto" data-id="element-22">
              <CheckCircle2 className="text-emerald-600" size={32} data-id="element-23" />
            </div>
            <p className="text-slate-600" data-id="element-24">
              We've sent a verification link to <strong data-id="element-25">{email}</strong>
            </p>
            <p className="text-sm text-slate-500" data-id="element-26">
              Click the link in your email to{' '}
              {mode === 'reset' ? 'reset your password' : 'verify your account'}
            </p>
            {verificationSent && <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-700" data-id="element-27">
                ✓ Verification email resent!
              </div>}
            <Button fullWidth variant="outline" onClick={handleResendVerification} data-id="element-28">
              Resend Verification Link
            </Button>
            <Button fullWidth onClick={onClose} data-id="element-29">
              Done
            </Button>
          </div> : <>
            {/* Social Auth Buttons */}
            <div className="space-y-3" data-id="element-30">
              <Button fullWidth variant="outline" className="gap-2 h-12" onClick={() => handleSocialAuth('google')} data-id="element-31">
                <svg className="w-5 h-5" viewBox="0 0 24 24" data-id="element-32">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" data-id="element-33" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" data-id="element-34" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" data-id="element-35" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" data-id="element-36" />
                </svg>
                Continue with Google
              </Button>

              <Button fullWidth variant="outline" className="gap-2 h-12" onClick={() => handleSocialAuth('microsoft')} data-id="element-37">
                <svg className="w-5 h-5" viewBox="0 0 23 23" data-id="element-38">
                  <path fill="#f3f3f3" d="M0 0h23v23H0z" data-id="element-39" />
                  <path fill="#f35325" d="M1 1h10v10H1z" data-id="element-40" />
                  <path fill="#81bc06" d="M12 1h10v10H12z" data-id="element-41" />
                  <path fill="#05a6f0" d="M1 12h10v10H1z" data-id="element-42" />
                  <path fill="#ffba08" d="M12 12h10v10H12z" data-id="element-43" />
                </svg>
                Continue with Microsoft
              </Button>

              <Button fullWidth variant="outline" className="gap-2 h-12" onClick={() => handleSocialAuth('apple')} data-id="element-44">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" data-id="element-45">
                  <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" data-id="element-46" />
                </svg>
                Continue with Apple
              </Button>
            </div>

            <div className="relative" data-id="element-47">
              <div className="absolute inset-0 flex items-center" data-id="element-48">
                <div className="w-full border-t border-slate-200" data-id="element-49"></div>
              </div>
              <div className="relative flex justify-center text-xs uppercase" data-id="element-50">
                <span className="bg-white px-2 text-slate-500" data-id="element-51">
                  Or continue with email
                </span>
              </div>
            </div>

            {/* Email Form */}
            <form onSubmit={handleEmailAuth} className="space-y-4" data-id="element-52">
              {mode === 'signup' && <div data-id="element-53">
                  <label className="block text-sm font-semibold text-slate-700 mb-2" data-id="element-54">
                    Name
                  </label>
                  <div className="relative" data-id="element-55">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} data-id="element-56" />
                    <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Your name" className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-57" />
                  </div>
                </div>}

              <div data-id="element-58">
                <label className="block text-sm font-semibold text-slate-700 mb-2" data-id="element-59">
                  Email
                </label>
                <div className="relative" data-id="element-60">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} data-id="element-61" />
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-62" />
                </div>
              </div>

              {mode !== 'reset' && <div data-id="element-63">
                  <label className="block text-sm font-semibold text-slate-700 mb-2" data-id="element-64">
                    Password
                  </label>
                  <div className="relative" data-id="element-65">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} data-id="element-66" />
                    <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-67" />
                  </div>
                </div>}

              {error && <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700" data-id="element-68">
                  {error}
                </div>}

              {mode === 'signin' && <button type="button" onClick={() => setMode('reset')} className="text-sm text-blue-600 hover:text-blue-700 font-semibold" data-id="element-69">
                  Forgot password?
                </button>}

              <Button fullWidth size="xl" type="submit" data-id="element-70">
                {mode === 'signin' && 'Sign In'}
                {mode === 'signup' && 'Create Account'}
                {mode === 'reset' && 'Send Reset Link'}
              </Button>
            </form>

            {/* Toggle Mode */}
            <div className="text-center text-sm" data-id="element-71">
              {mode === 'signin' ? <span className="text-slate-600" data-id="element-72">
                  Don't have an account?{' '}
                  <button onClick={() => setMode('signup')} className="text-blue-600 hover:text-blue-700 font-semibold" data-id="element-73">
                    Sign up
                  </button>
                </span> : mode === 'signup' ? <span className="text-slate-600" data-id="element-74">
                  Already have an account?{' '}
                  <button onClick={() => setMode('signin')} className="text-blue-600 hover:text-blue-700 font-semibold" data-id="element-75">
                    Sign in
                  </button>
                </span> : null}
            </div>
          </>}
      </div>
    </Modal>;
}